import React, { useState } from 'react';
import ImageUpload from '../ImageUpload';
import ResponseDisplay from '../ResponseDisplay';
import borerApiService from '../../services/borerApiService';
import { useAuth } from '../../context/AuthContext';

const BorerControlForm = () => {
  useAuth();
  const [formData, setFormData] = useState({
    companyId: 1,
    companyUnitId: 1,
    companyFarmId: 1,
    trickSectionFieldId: 1,
    borerTrapId: '',
    latitude: '',
    longitude: '',
    collectDateTime: new Date().toISOString().slice(0, 19) + 'Z'
  });

  const [images, setImages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [requestPayload, setRequestPayload] = useState(null);
  const [fullRequestPayload, setFullRequestPayload] = useState(null);

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? (value === '' ? '' : Number(value)) : value
    }));

    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleImagesChange = (base64Images) => {
    setImages(base64Images);
    if (validationErrors.images) {
      setValidationErrors(prev => ({ ...prev, images: null }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.companyId || formData.companyId <= 0) {
      errors.companyId = 'Company ID must be a positive number';
    }
    if (!formData.companyUnitId || formData.companyUnitId <= 0) {
      errors.companyUnitId = 'Company Unit ID must be a positive number';
    }
    if (!formData.companyFarmId || formData.companyFarmId <= 0) {
      errors.companyFarmId = 'Company Farm ID must be a positive number';
    }
    if (!formData.trickSectionFieldId || formData.trickSectionFieldId <= 0) {
      errors.trickSectionFieldId = 'Trick Section Field ID must be a positive number';
    }

    if (formData.latitude !== '') {
      const lat = parseFloat(formData.latitude);
      if (isNaN(lat) || lat < -90 || lat > 90) {
        errors.latitude = 'Latitude must be between -90 and 90';
      }
    }

    if (formData.longitude !== '') {
      const lng = parseFloat(formData.longitude);
      if (isNaN(lng) || lng < -180 || lng > 180) {
        errors.longitude = 'Longitude must be between -180 and 180';
      }
    }

    if (!formData.collectDateTime) {
      errors.collectDateTime = 'Collection date and time is required';
    }

    if (images.length !== 1) {
      errors.images = 'Exatamente 1 foto é obrigatória';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setResponse(null);
    setError(null);

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const requestData = {
        photo01: images[0] || '',
        companyId: Number(formData.companyId),
        companyUnitId: Number(formData.companyUnitId),
        companyFarmId: Number(formData.companyFarmId),
        trickSectionFieldId: Number(formData.trickSectionFieldId),
        borerTrapId: formData.borerTrapId !== '' ? Number(formData.borerTrapId) : null,
        latitude: formData.latitude || null,
        longitude: formData.longitude || null,
        collectDateTime: formData.collectDateTime,
      };

      const displayPayload = {
        ...requestData,
        photo01: requestData.photo01
          ? `${requestData.photo01.substring(0, 100)}... [${requestData.photo01.length} chars]`
          : '',
      };
      setRequestPayload(displayPayload);
      setFullRequestPayload(requestData);

      const result = await borerApiService.submitDetectorRequest(requestData);

      if (result.success) {
        setResponse(result);
        setError(null);
      } else {
        setError(result.error);
        setResponse(result);
      }
    } catch (err) {
      setError(err.message || 'An unexpected error occurred');
      setResponse(null);
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      companyId: 1,
      companyUnitId: 1,
      companyFarmId: 1,
      trickSectionFieldId: 1,
      borerTrapId: '',
      latitude: '',
      longitude: '',
      collectDateTime: new Date().toISOString().slice(0, 19) + 'Z'
    });
    setImages([]);
    setResponse(null);
    setError(null);
    setValidationErrors({});
    setRequestPayload(null);
    setFullRequestPayload(null);
  };

  const getInputClassName = (fieldName) => {
    return `form-control ${validationErrors[fieldName] ? 'is-invalid' : ''}`;
  };

  const downloadPayload = () => {
    if (!fullRequestPayload) return;

    const dataStr = JSON.stringify(fullRequestPayload, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `borercontrol-request-${new Date().getTime()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="container-fluid">
      <div className="row justify-content-center">
        <div className="col-lg-8">
          <div className="form-section">
            <div className="text-center mb-4">
              <h2 style={{ color: '#198754' }}>Broca Control API Tester</h2>
              <p className="text-muted">
                Test the Broca Control indirect monitoring detector request endpoint
              </p>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <ImageUpload
                  onImagesChange={handleImagesChange}
                  maxImages={1}
                />
                {validationErrors.images && (
                  <div className="text-danger small mt-1">{validationErrors.images}</div>
                )}
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  <label htmlFor="companyId" className="form-label">Company ID</label>
                  <input
                    type="number"
                    className={getInputClassName('companyId')}
                    id="companyId"
                    name="companyId"
                    value={formData.companyId}
                    onChange={handleInputChange}
                    min="1"
                    required
                  />
                  {validationErrors.companyId && (
                    <div className="invalid-feedback">{validationErrors.companyId}</div>
                  )}
                </div>
                <div className="col-md-6">
                  <label htmlFor="companyUnitId" className="form-label">Company Unit ID</label>
                  <input
                    type="number"
                    className={getInputClassName('companyUnitId')}
                    id="companyUnitId"
                    name="companyUnitId"
                    value={formData.companyUnitId}
                    onChange={handleInputChange}
                    min="1"
                    required
                  />
                  {validationErrors.companyUnitId && (
                    <div className="invalid-feedback">{validationErrors.companyUnitId}</div>
                  )}
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  <label htmlFor="companyFarmId" className="form-label">Company Farm ID</label>
                  <input
                    type="number"
                    className={getInputClassName('companyFarmId')}
                    id="companyFarmId"
                    name="companyFarmId"
                    value={formData.companyFarmId}
                    onChange={handleInputChange}
                    min="1"
                    required
                  />
                  {validationErrors.companyFarmId && (
                    <div className="invalid-feedback">{validationErrors.companyFarmId}</div>
                  )}
                </div>
                <div className="col-md-6">
                  <label htmlFor="trickSectionFieldId" className="form-label">Trick Section Field ID</label>
                  <input
                    type="number"
                    className={getInputClassName('trickSectionFieldId')}
                    id="trickSectionFieldId"
                    name="trickSectionFieldId"
                    value={formData.trickSectionFieldId}
                    onChange={handleInputChange}
                    min="1"
                    required
                  />
                  {validationErrors.trickSectionFieldId && (
                    <div className="invalid-feedback">{validationErrors.trickSectionFieldId}</div>
                  )}
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  <label htmlFor="borerTrapId" className="form-label">Borer Trap ID <span className="text-muted">(opcional)</span></label>
                  <input
                    type="number"
                    className={getInputClassName('borerTrapId')}
                    id="borerTrapId"
                    name="borerTrapId"
                    value={formData.borerTrapId}
                    onChange={handleInputChange}
                    min="1"
                  />
                  {validationErrors.borerTrapId && (
                    <div className="invalid-feedback">{validationErrors.borerTrapId}</div>
                  )}
                </div>
                <div className="col-md-6">
                  <label htmlFor="collectDateTime" className="form-label">Collection Date &amp; Time</label>
                  <input
                    type="datetime-local"
                    className={getInputClassName('collectDateTime')}
                    id="collectDateTime"
                    name="collectDateTime"
                    value={formData.collectDateTime.slice(0, 19)}
                    onChange={(e) => handleInputChange({
                      target: {
                        name: 'collectDateTime',
                        value: e.target.value + 'Z',
                        type: 'text'
                      }
                    })}
                    required
                  />
                  {validationErrors.collectDateTime && (
                    <div className="invalid-feedback">{validationErrors.collectDateTime}</div>
                  )}
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  <label htmlFor="latitude" className="form-label">Latitude <span className="text-muted">(opcional)</span></label>
                  <input
                    type="text"
                    className={getInputClassName('latitude')}
                    id="latitude"
                    name="latitude"
                    value={formData.latitude}
                    onChange={handleInputChange}
                    placeholder="-60.5510"
                  />
                  {validationErrors.latitude && (
                    <div className="invalid-feedback">{validationErrors.latitude}</div>
                  )}
                </div>
                <div className="col-md-6">
                  <label htmlFor="longitude" className="form-label">Longitude <span className="text-muted">(opcional)</span></label>
                  <input
                    type="text"
                    className={getInputClassName('longitude')}
                    id="longitude"
                    name="longitude"
                    value={formData.longitude}
                    onChange={handleInputChange}
                    placeholder="-80.6340"
                  />
                  {validationErrors.longitude && (
                    <div className="invalid-feedback">{validationErrors.longitude}</div>
                  )}
                </div>
              </div>

              <div className="d-flex gap-3 justify-content-between">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={resetForm}
                  disabled={isLoading}
                >
                  Reset Form
                </button>
                <button
                  type="submit"
                  className="btn btn-success"
                  disabled={isLoading || images.length !== 1}
                >
                  {isLoading ? (
                    <>
                      <span className="loading-spinner me-2"></span>
                      Sending Request...
                    </>
                  ) : (
                    'Submit Request'
                  )}
                </button>
              </div>
            </form>
          </div>

          {requestPayload && (
            <div className="response-container mt-3" style={{ borderLeft: '4px solid #198754' }}>
              <div className="d-flex align-items-start">
                <i className="bi bi-code-square text-success fs-4 me-3 mt-1"></i>
                <div className="flex-grow-1">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h5 className="text-success mb-0">Request Payload Sent</h5>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-success"
                      onClick={downloadPayload}
                      title="Download full payload with complete base64 image"
                    >
                      <i className="bi bi-download me-1"></i>
                      Download Full Payload
                    </button>
                  </div>
                  <p className="text-muted mb-2">
                    <small>
                      <strong>Endpoint:</strong> POST{' '}
                      <code>https://api-borer-dev.azurewebsites.net/api/indirectmonitoring/borer-detector-request</code>
                    </small>
                  </p>
                  <div className="json-display">
                    {JSON.stringify(requestPayload, null, 2)}
                  </div>
                </div>
              </div>
            </div>
          )}

          <ResponseDisplay
            response={response}
            isLoading={isLoading}
            error={error}
          />
        </div>
      </div>
    </div>
  );
};

export default BorerControlForm;
